import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Tooltip,
  useTheme,
  alpha,
  Chip,
  Menu,
  MenuItem
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Image as ImageIcon,
  Label as LabelIcon,
  Add as AddIcon
} from '@mui/icons-material';
// Note: Drag and drop will be implemented using @hello-pangea/dnd in the parent component
import useTaskStore from '../../../stores/taskStore';
import SecureAvatar from '../../ui/SecureAvatar';

// Platform aspect ratios
const PLATFORM_ASPECT_RATIOS = {
  instagram: '4/5',    // Instagram's current ratio
  facebook: '1/1',     // Square
  twitter: '16/9',     // Landscape
  linkedin: '1.91/1',  // LinkedIn landscape
  tiktok: '9/16',     // Vertical for TikTok
  youtube: '16/9'      // YouTube landscape
};

const GridCell = ({ 
  row, 
  column, 
  cellData, 
  tags, 
  platform = 'instagram', // Default to Instagram
  isSelected,
  onClick,
  onTaskRemove,
  onDrop,
  onTagAssign,
  onCreateTask
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [tagMenuAnchor, setTagMenuAnchor] = useState(null);

  // Handle drag start (when dragging from this cell)
  const handleDragStart = (e) => {
    if (task) {
      e.dataTransfer.effectAllowed = 'move';
      // Include cell position in the drag data to identify cell-to-cell moves
      const dragData = {
        taskId: task._id,
        fromRow: row,
        fromColumn: column,
        isFromGrid: true
      };
      e.dataTransfer.setData('gridCell', JSON.stringify(dragData));
    }
  };

  const handleDragEnd = (e) => {
    setIsDragOver(false);
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    // Check if dragging from another grid cell
    const gridCellData = e.dataTransfer.getData('gridCell');
    if (gridCellData) {
      const { taskId, fromRow, fromColumn } = JSON.parse(gridCellData);
      // Only trigger move if dropping on a different cell
      if (fromRow !== row || fromColumn !== column) {
        if (onDrop) {
          onDrop(taskId, row, column, fromRow, fromColumn);
        }
      }
      return;
    }

    // Check if dragging from sidebar
    const taskData = e.dataTransfer.getData('task');
    if (taskData) {
      const task = JSON.parse(taskData);
      if (onDrop) {
        onDrop(task._id, row, column);
      }
    }
  };

  // Get tag info for this cell
  const cellTag = cellData?.tagId ? tags.find(tag => tag._id === cellData.tagId) : null;
  const task = cellData?.taskIds?.[0]; // For now, handling single task mode
  
  // Check if task has image attachment (moved to component level for broader access)
  const imageAttachment = task?.attachments?.find(
    att => att.file?.mimetype?.startsWith('image/')
  );

  // Handle menu
  const handleMenuOpen = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTagMenuOpen = (event) => {
    event.stopPropagation();
    event.preventDefault();
    handleMenuClose(); // Close the actions menu first
    setTagMenuAnchor(event.currentTarget);
  };

  const handleTagMenuClose = () => {
    setTagMenuAnchor(null);
  };

  const handleTagSelect = (tagId) => {
    if (onTagAssign) {
      onTagAssign(row, column, tagId);
    }
    handleTagMenuClose();
  };

  const handleViewTask = useCallback(() => {
    if (task) {
      const { openTaskDrawer } = useTaskStore.getState();
      if (openTaskDrawer) {
        // Open the task drawer with project/section fields hidden (same as Kanban)
        openTaskDrawer(task, {
          hideProjectField: true,
          hideSectionField: true
        });
      }
    }
    handleMenuClose();
  }, [task]);

  const handleCreateTask = useCallback(() => {
    if (onCreateTask) {
      onCreateTask(row, column);
    }
    handleMenuClose();
  }, [row, column, onCreateTask]);

  const handleRemoveTask = () => {
    if (task && onTaskRemove) {
      onTaskRemove(task._id, row, column);
    }
    handleMenuClose();
  };

  // Determine cell styling
  const getCellStyle = () => {
    const baseStyle = {
      aspectRatio: PLATFORM_ASPECT_RATIOS[platform] || '4/5',
      position: 'relative',
      cursor: task ? 'grab' : 'pointer', // Show grab cursor for draggable cells
      transition: 'all 0.2s ease',
      overflow: 'hidden',
      border: '1px solid',
      borderColor: alpha(theme.palette.divider, 0.1),
      borderRadius: 0,
      '&:active': task ? {
        cursor: 'grabbing' // Show grabbing cursor while dragging
      } : {}
    };

    if (isSelected) {
      baseStyle.borderColor = theme.palette.primary.main;
      baseStyle.borderWidth = '2px';
      baseStyle.boxShadow = `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`;
    }

    if (cellTag) {
      baseStyle.borderColor = alpha(cellTag.color, 0.4);
      // Wider border for cells with images and tags
      baseStyle.borderWidth = task && imageAttachment ? '4px' : '2px';
    }

    if (isDragOver) {
      baseStyle.backgroundColor = alpha(theme.palette.primary.main, 0.1);
      baseStyle.borderColor = theme.palette.primary.main;
      baseStyle.borderWidth = '2px';
    }

    return baseStyle;
  };

  // Render task content
  const renderTaskContent = () => {
    if (!task) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1,
            backgroundColor: alpha(theme.palette.action.hover, 0.05)
          }}
        >
          <ImageIcon 
            sx={{ 
              fontSize: 48, 
              color: theme.palette.action.disabled,
              mb: 1
            }} 
          />
          <Typography variant="caption" color="textSecondary">
            Drop task here
          </Typography>
        </Box>
      );
    }

    console.log("🚀 ~ renderTaskContent ~ imageAttachment:", imageAttachment)

    if (imageAttachment) {
      // Render with image using SecureAvatar - Instagram-style image display
      // Uses object-fit: cover with center positioning to match Instagram's exact cropping behavior
      // Supports all orientations (portrait, landscape, square) with center focus
      return (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            display: 'block',
            backgroundColor: '#f8f9fa'
          }}
        >
          <SecureAvatar
            src={imageAttachment.file}
            alt={task.title}
            version="preview"
            variant="rectangular"
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 0,
              display: 'block',
              '& img': {
                objectFit: 'cover',
                objectPosition: 'center center',
                width: '100%',
                height: '100%',
                transform: 'translateZ(0)',
                backfaceVisibility: 'hidden'
              }
            }}
            imgProps={{
              style: {
                objectFit: 'cover',
                objectPosition: 'center center',
                width: '100%',
                height: '100%',
                display: 'block',
                transform: 'translateZ(0)',
                backfaceVisibility: 'hidden'
              }
            }}
          />
          
          {/* Dark overlay for better text readability */}
          {/* <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '25%',
              background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
              zIndex: 1
            }}
          /> */}
          
          {/* Task info overlay */}
          {/* <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 0.5,
              zIndex: 2
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'white',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 'medium',
                fontSize: '0.7rem'
              }}
            >
              {task.title}
            </Typography>
          </Box> */}
        </Box>
      );
    } else {
      // Render placeholder with TOV (Text on Visual)
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            backgroundImage: `linear-gradient(135deg, 
              ${alpha(theme.palette.primary.main, 0.05)} 0%, 
              ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
          }}
        >
          {/* TOV - Text on Visual */}
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              // color: theme.palette.primary.main,
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.2
            }}
          >
            {task.title}
          </Typography>
          
          {/* Task description/caption preview */}
          {task.description && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {task.description}
            </Typography>
          )}
        </Box>
      );
    }
  };

  // Handle cell click - open task drawer if task exists, otherwise just select the cell
  const handleCellClick = useCallback((e) => {
    // Don't open drawer if clicking on action buttons, menus, or if a menu is open
    if (e.target.closest('button') || 
        e.target.closest('[role="menu"]') || 
        e.target.closest('[role="menuitem"]') ||
        e.target.closest('.MuiMenu-root') ||
        e.target.closest('.MuiPopover-root') ||
        Boolean(anchorEl) || 
        Boolean(tagMenuAnchor)) {
      return;
    }
    
    if (task) {
      // Open task drawer
      const { openTaskDrawer } = useTaskStore.getState();
      if (openTaskDrawer) {
        openTaskDrawer(task, {
          hideProjectField: true,
          hideSectionField: true
        });
      }
    } else {
      // Just select the cell if no task
      onClick();
    }
  }, [task, onClick, anchorEl, tagMenuAnchor]);

  return (
    <Paper
      elevation={0}
      sx={getCellStyle()}
      onClick={handleCellClick}
      draggable={!!task}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Tag indicator - commented out when image is present to avoid overlay */}
      {cellTag && !imageAttachment && (
        <Chip
          label={cellTag.name}
          size="small"
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            zIndex: 2,
            backgroundColor: alpha(cellTag.color, 0.9),
            color: theme.palette.getContrastText(cellTag.color),
            fontWeight: 'medium',
            fontSize: '0.65rem',
            height: 18
          }}
        />
      )}
      {/* Commented out tag chip overlay for images */}
      {/* {cellTag && imageAttachment && (
        <Chip
          label={cellTag.name}
          size="small"
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            zIndex: 2,
            backgroundColor: alpha(cellTag.color, 0.9),
            color: theme.palette.getContrastText(cellTag.color),
            fontWeight: 'medium',
            fontSize: '0.65rem',
            height: 18
          }}
        />
      )} */}

      {/* Actions menu */}
      <IconButton
        size="small"
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          zIndex: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          width: 24,
          height: 24,
          '&:hover': {
            backgroundColor: alpha(theme.palette.background.paper, 0.95)
          }
        }}
        onClick={handleMenuOpen}
      >
        <MoreIcon fontSize="inherit" />
      </IconButton>

      {/* Main content */}
      {renderTaskContent()}

      {/* Drag over indicator */}
      {isDragOver && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            border: `2px dashed ${theme.palette.primary.main}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography variant="body2" color="primary">
            Drop here
          </Typography>
        </Box>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {!task && (
          <MenuItem onClick={handleCreateTask}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />
            Create Task
          </MenuItem>
        )}
        <MenuItem onClick={handleTagMenuOpen}>
          <LabelIcon fontSize="small" sx={{ mr: 1 }} />
          {cellTag ? 'Change Tag' : 'Assign Tag'}
        </MenuItem>
        {task && (
          <>
            <MenuItem divider />
            <MenuItem onClick={handleViewTask}>
              <ViewIcon fontSize="small" sx={{ mr: 1 }} />
              View Task
            </MenuItem>
            <MenuItem onClick={handleRemoveTask} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Remove from Grid
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Tag Assignment Menu */}
      <Menu
        anchorEl={tagMenuAnchor}
        open={Boolean(tagMenuAnchor)}
        onClose={handleTagMenuClose}
      >
        <MenuItem disabled>
          <Typography variant="caption">Select a tag</Typography>
        </MenuItem>
        {tags.map((tag) => (
          <MenuItem key={tag._id} onClick={() => handleTagSelect(tag._id)}>
            <Chip
              label={tag.name}
              size="small"
              sx={{
                backgroundColor: tag.color,
                color: theme.palette.getContrastText(tag.color),
                mr: 1
              }}
            />
            {cellTag?._id === tag._id && (
              <Typography variant="caption" color="textSecondary">
                (Current)
              </Typography>
            )}
          </MenuItem>
        ))}
        {cellTag && (
          <>
            <MenuItem divider />
            <MenuItem onClick={() => handleTagSelect(null)}>
              <Typography variant="body2" color="textSecondary">
                Remove Tag
              </Typography>
            </MenuItem>
          </>
        )}
      </Menu>
    </Paper>
  );
};

export default GridCell;